import {
  fetchCategoriesRequest,
  fetchProductsRequest,
  fetchProductRequest,
  createCategoryRequest,
  createProductRequest,
  updateProductRequest,
  deleteProductImageRequest
} from "../api/index";
import { handleErrorRequest } from "../helpers/api";
import {
  payloadIdentity,
  simplePayloadMutation,
  resetMutation,
  loadingMutation
} from "../helpers/redux";
import _ from "lodash";
import { createActions, handleActions } from "redux-actions";
import { getJWT } from "./auth";
import { createSelector } from "reselect";

const marketState = state => state.market;
const getValue = key =>
  createSelector(
    marketState,
    state => _.get(state, key)
  );

export const getCategories = getValue("categories");
export const isFetchingCategories = getValue("loadingFetchCategories");
export const getFetchCategoriesFailure = getValue("failureFetchCategories");
export const isSuccessFetchCategories = getValue("successFetchCategories");
export const isSubmittingCategory = getValue("loadingSubmitCategory");
export const getSubmitCategoryFailure = getValue("failureSubmitCategory");

export const getProducts = getValue("products");
export const isFetchingProducts = getValue("loadingFetchProducts");
export const isSuccessFetchProducts = getValue("successFetchProducts");
export const getFetchProductsFailure = getValue("failureFetchProducts");
export const isSubmittingProduct = getValue("loadingSubmitProduct");
export const getSubmitProductFailure = getValue("failureSubmitProduct");
export const getProductsTotalPages = getValue("totalPages");
export const getProductsCurrentPage = getValue("currentPage");
export const getProductsPerPage = getValue("perPage");
export const getProductFilter = getValue("filter");

export const getCurrentProduct = getValue("currentProduct");
export const isFetchingProduct = getValue("loadingFetchProduct");
export const isSuccessFetchProduct = getValue("successFetchProduct");
export const getFetchProductFailure = getValue("failureFetchProduct");
export const getUpdateProductFailure = getValue("failureUpdateProduct");
export const isSuccessUpdateProduct = getValue("successUpdateProduct");
export const isUpdatingProduct = getValue("loadingUpdatedProduct");

const createdActions = createActions({
  market: {
    FETCH_CATEGORIES_ATTEMPT: null,
    FETCH_CATEGORIES_FAILURE: payloadIdentity("failMessage"),
    FETCH_CATEGORIES_SUCCESS: null,
    SUBMIT_CATEGORY_ATTEMPT: null,
    SUBMIT_CATEGORY_FAILURE: payloadIdentity("failMessage"),
    SUBMIT_CATEGORY_SUCCESS: null,
    SET_CATEGORIES: payloadIdentity("categories"),
    FETCH_PRODUCTS_ATTEMPT: null,
    FETCH_PRODUCTS_FAILURE: payloadIdentity("failMessage"),
    FETCH_PRODUCTS_SUCCESS: null,
    FETCH_PRODUCT_ATTEMPT: null,
    FETCH_PRODUCT_FAILURE: payloadIdentity("failMessage"),
    FETCH_PRODUCT_SUCCESS: null,
    SUBMIT_PRODUCT_ATTEMPT: null,
    SUBMIT_PRODUCT_FAILURE: payloadIdentity("failMessage"),
    SUBMIT_PRODUCT_SUCCESS: null,
    SET_PRODUCTS: payloadIdentity("products"),
    SET_TOTAL_PAGES: payloadIdentity("totalPages"),
    SET_CURRENT_PAGE: payloadIdentity("currentPage"),
    SET_CURRENT_PRODUCT: payloadIdentity("currentProduct"),
    SET_PRODUCT_FILTER: payloadIdentity("filter"),
    DELETE_PRODUCT_IMAGE_ATTEMPT: null,
    DELETE_PRODUCT_IMAGE_FAILURE: payloadIdentity("failMessage"),
    DELETE_PRODUCT_IMAGE_SUCCESS: null,
    RESET_STATE: payloadIdentity("toOmit")
  }
});

const {
  fetchCategoriesAttempt,
  fetchCategoriesFailure,
  fetchCategoriesSuccess,
  submitCategoryAttempt,
  submitCategoryFailure,
  submitCategorySuccess,
  fetchProductsAttempt,
  fetchProductsFailure,
  fetchProductsSuccess,
  submitProductAttempt,
  submitProductFailure,
  submitProductSuccess,
  deleteProductImageAttempt,
  deleteProductImageSuccess,
  deleteProductImageFailure,
  setCategories,
  setProducts,
  fetchProductAttempt,
  fetchProductFailure,
  fetchProductSuccess,
  setCurrentProduct,
  setTotalPages
} = createdActions.market;

const fetchCategories = ({ dealer_id, query }) => (dispatch, getState) => {
  dispatch(fetchCategoriesAttempt());
  const jwt = getJWT(getState());
  return fetchCategoriesRequest({ dealer_id, jwt, query }).then(
    ({ response, error }) => {
      if (response) {
        dispatch(fetchCategoriesSuccess());
        dispatch(setCategories(response.data));
        return Promise.resolve({ response });
      } else {
        dispatch(fetchCategoriesFailure(handleErrorRequest(error)));
        return Promise.resolve({ error });
      }
    }
  );
};

const fetchProducts = ({ dealer_id, query }) => (dispatch, getState) => {
  dispatch(fetchProductsAttempt());
  return fetchProductsRequest({ dealer_id, query }).then(
    ({ response, error }) => {
      if (response) {
        dispatch(fetchProductsSuccess());
        dispatch(setProducts(response.data));
        dispatch(setTotalPages(parseInt(response.headers["x-total-pages"])));
        return Promise.resolve({ response });
      } else {
        dispatch(fetchProductsFailure(handleErrorRequest(error)));
        return Promise.resolve({ error });
      }
    }
  );
};

const fetchProduct = ({ dealerId, productId }) => async (
  dispatch,
  getState
) => {
  dispatch(fetchProductAttempt());
  const jwt = getJWT(getState());
  const { response, error } = await fetchProductRequest({
    dealerId,
    jwt,
    productId
  });
  if (response) {
    dispatch(fetchProductSuccess());
    dispatch(setCurrentProduct(response.data));
    return Promise.resolve({ response });
  } else {
    dispatch(fetchProductFailure(handleErrorRequest(error)));
    return Promise.resolve({ error });
  }
};

const createCategory = ({ dealer_id, data }) => (dispatch, getState) => {
  dispatch(submitCategoryAttempt());
  const jwt = getJWT(getState());
  return createCategoryRequest({ dealer_id, data, jwt }).then(
    ({ response, error }) => {
      if (response) {
        const categories = getCategories(getState());
        dispatch(submitCategorySuccess());
        dispatch(setCategories([...categories, response.data]));
        return Promise.resolve({ response });
      } else {
        dispatch(submitCategoryFailure(handleErrorRequest(error)));
        return Promise.resolve({ error });
      }
    }
  );
};

const createProduct = ({ dealer_id, data }) => (dispatch, getState) => {
  dispatch(submitProductAttempt());
  const jwt = getJWT(getState());
  return createProductRequest({ dealer_id, data, jwt }).then(
    ({ response, error }) => {
      if (response) {
        dispatch(submitProductSuccess());
        return Promise.resolve({ response });
      } else {
        dispatch(submitProductFailure(handleErrorRequest(error)));
        return Promise.resolve({ error });
      }
    }
  );
};

const updateProduct = ({ dealer_id, productId, data }) => (dispatch, getState) => {
  dispatch(submitProductAttempt());
  const jwt = getJWT(getState());
  return updateProductRequest({ dealer_id, productId, data, jwt }).then(
    ({ response, error }) => {
      if (response) {
        dispatch(submitProductSuccess());
        return Promise.resolve({ response });
      } else {
        dispatch(submitProductFailure(handleErrorRequest(error)));
        return Promise.resolve({ error });
      }
    }
  );
};

// const deleteProductImage = (dealer_id, product_id, image_id) => (dispatch, getState) => {
//   console.log('on deleteProductImage')
//   dispatch(deleteProductImageAttempt());
//   const jwt = getJWT(getState());
//   return deleteProductImageRequest({ dealer_id, product_id, image_id, jwt }).then(
//     ({ response, error }) => {
//       if (response) {
//         dispatch(deleteProductImageSuccess());
//         return Promise.resolve({ response });
//       } else {
//         dispatch(deleteProductImageFailure(handleErrorRequest(error)));
//         return Promise.resolve({ error });
//       }
//     }
//   );
// };

const deleteProductImage = (dealer_id, product_id, image_id ) => async (
  dispatch,
  getState
) => {
  console.log('on deleteProductImage')
  dispatch(deleteProductImageAttempt());
  const jwt = getJWT(getState());
  const { response, error } = await deleteProductImageRequest({
    dealer_id,
    product_id,
    image_id,
    jwt
  });
  if (response) {
    dispatch(deleteProductImageSuccess());
    return Promise.resolve({ response });
  } else {
    dispatch(deleteProductImageFailure(handleErrorRequest(error)));
    return Promise.resolve({ error });
  }
};

export const initialState = {
  loadingFetchCategories: false,
  failureFetchCategories: null,
  successFetchCategories: null,
  loadingSubmitCategory: false,
  failureSubmitCategory: null,
  successSubmitCategory: null,
  categories: [],
  loadingSubmitProduct: false,
  failureSubmitProduct: null,
  successSubmitProduct: null,
  loadingFetchProducts: false,
  failureFetchProducts: null,
  successFetchProducts: null,
  loadingFetchProduct: false,
  failureFetchProduct: null,
  successFetchProduct: null,
  failureUpdateProduct: null,
  successUpdateProduct: null,
  loadingUpdatedProduct: false,
  totalPages: 1,
  currentPage: 1,
  perPage: 10,
  filter: {
    name: ""
  }
};

export const actions = {
  ...createdActions.market,
  fetchCategories,
  createCategory,
  createProduct,
  updateProduct,
  deleteProductImage,
  fetchProducts,
  fetchProduct
};

export default handleActions(
  {
    market: {
      FETCH_CATEGORIES_ATTEMPT: loadingMutation({
        name: "FetchCategories",
        type: "loading"
      }),
      FETCH_CATEGORIES_FAILURE: loadingMutation({
        name: "FetchCategories",
        type: "failure"
      }),
      FETCH_CATEGORIES_SUCCESS: loadingMutation({
        name: "FetchCategories",
        type: "success"
      }),
      SUBMIT_CATEGORY_ATTEMPT: loadingMutation({
        name: "SubmitCategory",
        type: "loading"
      }),
      SUBMIT_CATEGORY_FAILURE: loadingMutation({
        name: "SubmitCategory",
        type: "failure"
      }),
      SUBMIT_CATEGORY_SUCCESS: loadingMutation({
        name: "SubmitCategory",
        type: "success"
      }),
      FETCH_PRODUCTS_ATTEMPT: loadingMutation({
        name: "FetchProducts",
        type: "loading"
      }),
      FETCH_PRODUCTS_FAILURE: loadingMutation({
        name: "FetchProducts",
        type: "failure"
      }),
      FETCH_PRODUCTS_SUCCESS: loadingMutation({
        name: "FetchProducts",
        type: "success"
      }),
      FETCH_PRODUCT_ATTEMPT: loadingMutation({
        name: "FetchProduct",
        type: "loading"
      }),
      FETCH_PRODUCT_FAILURE: loadingMutation({
        name: "FetchProduct",
        type: "failure"
      }),
      FETCH_PRODUCT_SUCCESS: loadingMutation({
        name: "FetchProduct",
        type: "success"
      }),
      SUBMIT_PRODUCT_ATTEMPT: loadingMutation({
        name: "SubmitProduct",
        type: "loading"
      }),
      SUBMIT_PRODUCT_FAILURE: loadingMutation({
        name: "SubmitProduct",
        type: "failure"
      }),
      SUBMIT_PRODUCT_SUCCESS: loadingMutation({
        name: "SubmitProduct",
        type: "success"
      }),
      DELETE_PRODUCT_IMAGE_SUCCESS: loadingMutation({
        name: "DeleteProductImage",
        type: "success"
      }),
      DELETE_PRODUCT_IMAGE_FAILURE: loadingMutation({
        name: "DeleteProductImage",
        type: "failure"
      }),
      DELETE_PRODUCT_IMAGE_ATTEMPT: loadingMutation({
        name: "DeleteProductImage",
        type: "loading"
      }),
      SET_CATEGORIES: simplePayloadMutation,
      SET_TOTAL_PAGES: simplePayloadMutation,
      SET_CURRENT_PAGE: simplePayloadMutation,
      SET_PRODUCTS: simplePayloadMutation,
      SET_CURRENT_PRODUCT: simplePayloadMutation,
      SET_PRODUCT_FILTER: simplePayloadMutation,
      RESET_STATE: resetMutation(initialState)
    }
  },
  initialState
);
